import {
    type RawHeadingData,
    type RawParagraphData,
    type RawPreambleData,
    type RawQuoteData,
    type RawSubHeadingData,
    type RawTestimonialData,
} from '@activebrands/core-web/libs/storyblok/storyblok';
import transformHeading, { type HeadingType } from '@activebrands/core-web/libs/storyblok/utils/transform-heading';
import transformParagraph, {
    type ParagraphType,
} from '@activebrands/core-web/libs/storyblok/utils/transform-paragraph';
import transformPreamble, { type PreambleType } from '@activebrands/core-web/libs/storyblok/utils/transform-preamble';
import transformQuote, { type QuoteType } from '@activebrands/core-web/libs/storyblok/utils/transform-quote';
import transformSubHeading, {
    type SubHeadingType,
} from '@activebrands/core-web/libs/storyblok/utils/transform-subheading';
import transformTestimonial, {
    type TestimonialType,
} from '@activebrands/core-web/libs/storyblok/utils/transform-testimonial';

type RawTextItem =
    | RawHeadingData
    | RawParagraphData
    | RawPreambleData
    | RawQuoteData
    | RawSubHeadingData
    | RawTestimonialData;

type TransformedTextItem = HeadingType | ParagraphType | PreambleType | QuoteType | SubHeadingType | TestimonialType;

type TransformTextItemsProps = {
    items: RawTextItem[] | undefined;
};

/**
 * Transforms an array of raw text items from Storyblok into standardized text item objects
 *
 * @param props - The transformation parameters
 * @param props.items - Array of raw text items to transform
 * @returns Array of transformed text items
 */
const transformTextItems = ({ items }: TransformTextItemsProps): TransformedTextItem[] => {
    if (!items || !Array.isArray(items) || items.length === 0) {
        return [];
    }

    return items
        .map((item: RawTextItem): TransformedTextItem | null => {
            if (!item || !item._uid) {
                return null;
            }

            switch (item.component) {
                case 'heading':
                case 'headingStyled':
                    return (
                        transformHeading({
                            heading: item as RawHeadingData,
                            defaultType: 'h2',
                            defaultStyle: 'md',
                        }) || null
                    );

                case 'paragraph':
                    return (
                        transformParagraph({
                            paragraph: item as RawParagraphData,
                        }) || null
                    );

                case 'preamble':
                    return (
                        transformPreamble({
                            preamble: item as RawPreambleData,
                        }) || null
                    );

                case 'subHeading':
                    return (
                        transformSubHeading({
                            subHeading: item as RawSubHeadingData,
                            defaultType: 'h3',
                            defaultStyle: 'md',
                        }) || null
                    );

                case 'testimonial':
                    return (
                        transformTestimonial({
                            testimonial: item as RawTestimonialData,
                        }) || null
                    );

                case 'quote':
                    return (
                        transformQuote({
                            quote: item as RawQuoteData,
                        }) || null
                    );

                default:
                    console.warn(`Unknown text component type: ${(item as { component: string }).component}`);
                    return null;
            }
        })
        .filter((item): item is TransformedTextItem => item !== null);
};

export default transformTextItems;
